'use client';

import { useCallback, useEffect, useState } from 'react';

import { getSessionVariable, removeSessionVariable, setSessionVariable } from '@services/storage';
import { TradeInEligibilityContext, getEasyTradeInEligibility } from '@services/trade-in';

import { EasyTradeInChoice, EasyTradeInEligibility, EasyTradeInResult } from '@app-types/easy-trade-in';

import { EASY_TRADE_IN_SESSION_KEY } from '@constants/easy-trade-in';

import { useQuery } from '@tanstack/react-query';

const NO_ELIGIBILITY: EasyTradeInEligibility = { hasClassicTradeIn: false, hasEasyTradeIn: false };
export type UseEasyTradeInReturn = ReturnType<typeof useEasyTradeIn>;
type EasyTradeInSessionState = {
    choice: EasyTradeInChoice;
    confirmedResult: EasyTradeInResult | null;
};

function readSessionState(): EasyTradeInSessionState | undefined {
    try {
        const raw = getSessionVariable(EASY_TRADE_IN_SESSION_KEY);
        return raw ? (JSON.parse(raw) as EasyTradeInSessionState) : undefined;
    } catch {
        return undefined;
    }
}

function writeSessionState(state: EasyTradeInSessionState) {
    try {
        setSessionVariable(EASY_TRADE_IN_SESSION_KEY, JSON.stringify(state));
    } catch (err) {
        console.error('Failed to persist easy trade-in state:', err);
    }
}

type UseEasyTradeInProps = TradeInEligibilityContext & {
    enabled: boolean;
};

export function useEasyTradeIn({ enabled, ...context }: UseEasyTradeInProps) {
    const { isRenewal, idPu, contractId } = context;

    const [choiceValue, setChoiceValue] = useState<EasyTradeInChoice>(EasyTradeInChoice.RETURN);
    const [confirmedResult, setConfirmedResult] = useState<EasyTradeInResult | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasReturnHint, setHasReturnHint] = useState(false);

    const eligibilityQuery = useQuery({
        queryKey: ['easy-trade-in-eligibility', isRenewal, contractId, idPu],
        queryFn: () => getEasyTradeInEligibility(context),
        enabled: enabled && (!isRenewal || !!contractId),
    });

    useEffect(() => {
        const session = readSessionState();
        if (session) {
            setChoiceValue(session.choice);
            setConfirmedResult(session.confirmedResult);
        }
    }, []);

    const setChoice = useCallback(
        (next: EasyTradeInChoice) => {
            setChoiceValue(next);
            writeSessionState({ choice: next, confirmedResult });
        },
        [confirmedResult],
    );

    const openModal = useCallback(() => setIsModalOpen(true), []);
    const closeModal = useCallback(() => setIsModalOpen(false), []);

    const triggerReturnHint = useCallback(() => setHasReturnHint(true), []);

    const confirm = useCallback((result: EasyTradeInResult) => {
        setConfirmedResult(result);
        setChoiceValue(EasyTradeInChoice.RETURN);
        writeSessionState({ choice: EasyTradeInChoice.RETURN, confirmedResult: result });
        setIsModalOpen(false);
        setHasReturnHint(false);
    }, []);

    const keepPhone = useCallback((result: EasyTradeInResult) => {
        setChoiceValue(EasyTradeInChoice.KEEP);
        setConfirmedResult(result);
        writeSessionState({ choice: EasyTradeInChoice.KEEP, confirmedResult: result });
        setIsModalOpen(false);
    }, []);

    const reset = useCallback(() => {
        setChoiceValue(EasyTradeInChoice.RETURN);
        setConfirmedResult(null);
        setHasReturnHint(false);
        removeSessionVariable(EASY_TRADE_IN_SESSION_KEY);
    }, []);

    const eligibility = eligibilityQuery.data ?? NO_ELIGIBILITY;
    const isConfirmed = choiceValue === EasyTradeInChoice.RETURN && confirmedResult !== null;

    return {
        eligibility,
        isEligibilityLoading: eligibilityQuery.isLoading,
        context,
        choice: choiceValue,
        setChoice,
        confirmedResult,
        isConfirmed,
        isModalOpen,
        openModal,
        closeModal,
        confirm,
        keepPhone,
        reset,
        hasReturnHint,
        triggerReturnHint,
    };
}
